import { Component, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { EventEmitter } from 'events';
import { compact, map, orderBy } from 'lodash';
import { takeUntil } from 'rxjs/operators';
import { IStudent } from 'Src/ng2/shared/typings/interfaces/student.interface';
import {
  getMadlibOptions,
  getMadlibOptionsLoadedStatus,
  ICreateSyncToolQueryField,
  LoadMadlibOptions,
} from 'Src/ng2/store';
import { IPickerOption } from 'Src/nvps-libraries/design/nv-multi-picker/nv-multi-picker.interface';
import { BaseModalComponent } from '../../base-modal.component';
import { ISchoolAssessment } from 'Src/ng2/shared/typings/interfaces/school-assessment.interface';
import { Observable } from 'rxjs';
import { IDropdownOption } from 'Src/ng2/shared/typings/interfaces/design-library.interface';

export interface ISheetModalComponentData {
  title: string;
  message: string;
  confirmText?: string;
}

export interface ISyncedSheetModalPayload {
  sheetName: string;
  studentsToInclude: string;
  studentCount: number;
  selectedOptions: string[];
  schoolId: string;
  schoolAssessments$?: Observable<ISchoolAssessment[]>;
}

const DIM1_MADLIB_PATHS =
  'studentDetails.classOf,' +
  'studentDetails.grade,' +
  'studentDetails.classOf,' +
  'studentDetails.officialClass,' +
  'pointPeople,' +
  'counselor,' +
  'gradPlanningDetails.plannedGraduationDate,' +
  'spedDetails.isSped,' +
  'ellDetails.isEll';

const FILTER_OPTIONS_MAPPING = {
  'By class': 'classOf',
  'By grade': 'grade',
  'By official class': 'officialClass',
};

const SECOND_DROPDOWN_MAPPING = {
  'By class': 'CLASSES',
  'By grade': 'GRADES',
  'By official class': 'OFFICIAL CLASSES',
};

@Component({
  template: require('./synced-sheet-modal.component.html'),
  styles: [require('./synced-sheet-modal.component.scss')],
})
export class SyncedSheetModalComponent extends BaseModalComponent implements OnInit, OnDestroy {
  @Input() students: IStudent[];
  @Output() dropdownListItem: EventEmitter = new EventEmitter();
  title = 'Create new sheet';
  form: FormGroup;
  sheetNamePlaceholder = 'Enter a sheet name...';
  itemCount: number = 0;
  itemType: string = 'Synced sheet';
  isProfileMode: boolean = true;
  showSecondDropdown: boolean = false;
  secondDropdownLabel: string = '';
  madlibOptions;
  extraQueryFields: ICreateSyncToolQueryField[] = [];
  requiredColsVals = ['Student ID', 'Student Name'];
  studentsOptions;
  studentsMap = {
    'By cohort': {},
    'By grade': {},
    'By official class': {},
  };
  availableOptions: IPickerOption[];
  selectedOptions: string[] = [];
  dropdownOptions: IDropdownOption[] = [
    {
      key: 'All students',
      human: 'All students',
    },
    {
      key: 'By class',
      human: 'By class',
    },
    {
      key: 'By grade',
      human: 'By grade',
    },
    {
      key: 'By official class',
      human: 'By official class',
    },
  ];
  currentSelection: string;
  small: string = 'small';
  info: string = 'info';
  allStudents;

  constructor(
    dialogRef: MatDialogRef<SyncedSheetModalComponent>,
    public formBuilder: FormBuilder,
    private store: Store<any>,
    @Inject(MAT_DIALOG_DATA) public data, // : ISpecializedDataType,
  ) {
    super(dialogRef);
  }

  get nextValid() {
    const sheetName = this.form.controls.sheetName.value;
    if (!sheetName) {
      return false;
    } else if (!sheetName.trim().length) {
      return false;
    } else if (this.showSecondDropdown) {
      return this.selectedOptions.length > 0;
    } else {
      return true;
    }
  }

  get studentCount() {
    let studentsToCount: [];
    const { allStudents } = this.data;
    let totalStudentsSelected;
    if (this._filterStudents(this.currentSelection) && this.selectedOptions.length > 0) {
      studentsToCount = allStudents.filter(student => {
        const loc = FILTER_OPTIONS_MAPPING[this.currentSelection];
        return this.selectedOptions.includes(student.studentDetails[loc]);
      });
      totalStudentsSelected = studentsToCount.length;
    } else if (this.showSecondDropdown) {
      totalStudentsSelected = 0;
    } else {
      totalStudentsSelected = allStudents.length;
    }
    return totalStudentsSelected;
  }

  ngOnInit() {
    const { schoolId, allStudents, currentSelection } = this.data;
    this.allStudents = allStudents;
    this.currentSelection = currentSelection;
    this.store
      .select(getMadlibOptionsLoadedStatus)
      .pipe(takeUntil(this.destroy$))
      .subscribe(loaded => {
        if (!loaded) {
          const payload = { schoolId, paths: DIM1_MADLIB_PATHS };
          this.store.dispatch(new LoadMadlibOptions(payload));
        } else {
          this.store
            .select(getMadlibOptions)
            .pipe(takeUntil(this.destroy$))
            .subscribe(madlibOptions => {
              this.madlibOptions = madlibOptions;
              this.setForm();
            });
        }
      });
  }

  setForm(): void {
    // set forms based on input data if returning from other modal
    const { sheetName, studentsToInclude, selectedOptions } = this.data;
    const parsedStudentsToInclude = studentsToInclude ? studentsToInclude : 'All students';
    this.selectedOptions = selectedOptions ? selectedOptions : [];
    if (this._filterStudents(parsedStudentsToInclude)) {
      this.concatAvailableOptions(this.madlibOptions, FILTER_OPTIONS_MAPPING[parsedStudentsToInclude]);
      this.showSecondDropdown = true;
    }
    this.form = this.formBuilder.group({
      sheetName: [sheetName, Validators.required],
      studentsToInclude: [parsedStudentsToInclude],
    });
  }

  concatAvailableOptions(arr: any, key: string): void {
    const availableOptions = map(arr[key], option => {
      if (option) {
        return {
          key: option,
          human: option,
        };
      }
    });
    this.availableOptions = compact(availableOptions);
  }

  getSortedGradeOptions(options: string[]): string[] {
    return options.sort((a, b) => Number(b) - Number(a));
  }

  getSortedClassOptions(options: string[]): string[] {
    return orderBy(options);
  }

  updateSelection($event) {
    this.selectedOptions = [];
    this.showSecondDropdown = this._filterStudents($event);
    this.currentSelection = $event;
    this.form.controls.studentsToInclude.setValue($event);
    this.secondDropdownLabel = SECOND_DROPDOWN_MAPPING[$event];
    this.concatAvailableOptions(this.madlibOptions, FILTER_OPTIONS_MAPPING[$event]);
  }

  _filterStudents(string) {
    return string !== 'All students' ? true : false;
  }

  ngOnDestroy() {
    //
  }

  submit() {
    if (this.nextValid) {
      // get data from the form
      const allStudents = this.allStudents;
      const sheetName = this.form.controls.sheetName.value;
      const studentsToInclude = this.form.controls.studentsToInclude.value;
      const selectedOptions = this.selectedOptions;
      const currentSelection = this.currentSelection;
      const payload = {
        allStudents,
        currentSelection,
        sheetName,
        studentsToInclude,
        selectedOptions,
        schoolId: this.data.schoolId,
      };
      super.close(payload);
    }
  }

  public close(): void {
    super.close();
  }
}
